import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal, Eyebrow } from "@/lib/motion";
import { PRICING } from "@/data";

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
      <div className="text-center md:text-left mb-14 md:mb-20">
        <Eyebrow>Pricing</Eyebrow>
        <h2 className="mt-5 font-display font-normal text-4xl md:text-5xl lg:text-6xl tracking-tight">
          Simple plans, no surprises.
        </h2>
      </div>

      <div className="grid md:grid-cols-3 gap-6 md:gap-8 items-start">
        {PRICING.map((p, i) => {
          const dim = p.featured ? "text-brand-cream/80" : "text-brand-ink/70";
          const dimmer = p.featured ? "text-brand-cream/60" : "text-brand-ink/55";
          return (
            <Reveal key={p.name} delay={i * 0.1}>
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ duration: 0.3 }}
                className={`h-full rounded-3xl border p-8 md:p-10 flex flex-col ${
                  p.featured
                    ? "bg-brand-teal text-brand-cream border-brand-teal"
                    : "bg-brand-cream text-brand-ink border-brand-peach"
                }`}
                data-testid={`pricing-${p.name.toLowerCase()}`}
              >
                {p.featured && (
                  <span className="self-start font-mono text-[10px] uppercase tracking-[0.2em] bg-brand-apricot text-brand-ink px-3 py-1 rounded-full mb-4">
                    ★ Most popular
                  </span>
                )}
                <h3 className="font-display text-3xl">{p.name}</h3>
                <p className={`mt-2 text-sm ${dim}`}>{p.tagline}</p>

                <div className="mt-6">
                  {p.implFee && (
                    <div className={`text-sm ${dimmer}`}>{p.implFee}</div>
                  )}
                  <div className="flex items-end gap-1">
                    <span className="font-display text-4xl md:text-5xl">{p.price}</span>
                    {p.cadence && (
                      <span className={`mb-2 text-sm ${dimmer}`}>{p.cadence}</span>
                    )}
                  </div>
                </div>

                {p.bestFor.length > 0 && (
                  <div className="mt-8">
                    <div className={`font-mono text-[10px] uppercase tracking-[0.2em] mb-3 ${dim}`}>Best for</div>
                    <ul className="space-y-2">
                      {p.bestFor.map((b) => (
                        <li key={b} className={p.featured ? "text-brand-cream/90" : "text-brand-ink/80"}>{b}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-8 flex-1">
                  <div className={`font-mono text-[10px] uppercase tracking-[0.2em] mb-3 ${dim}`}>Includes</div>
                  <ul className="space-y-3">
                    {p.includes.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <Check size={18} className={`${p.featured ? "text-brand-sage" : "text-brand-teal"} mt-0.5 shrink-0`} />
                        <span className={p.featured ? "text-brand-cream/90" : "text-brand-ink/80"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href="#book"
                  data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                  className={`mt-8 text-center rounded-full py-3 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                    p.featured
                      ? "bg-brand-cream text-brand-ink hover:bg-brand-apricot"
                      : "bg-brand-teal text-brand-cream hover:bg-brand-ink"
                  }`}
                >
                  {p.name === "Enterprise" ? "Talk to us" : "Get started"}
                </a>
              </motion.div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}
